import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, staggerParent, viewportOnce } from '@/lib/motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { HOME } from '@/constants/testIds';

const techTabs = [
  { id: 'ai', label: 'AI / ML' },
  { id: 'frontend', label: 'Frontend' },
  { id: 'backend', label: 'Backend' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'ecom', label: 'E-Commerce' },
  { id: 'db', label: 'Database' },
];

export default function Technologies({ data, items }) {
  if (!items?.length) return null;
  const title = data?.title;
  const subtitle = data?.subtitle;

  const techs = (() => {
    if (Array.isArray(items) && items.length) {
      const grouped = {};
      items.forEach((t) => {
        const key = t.category || 'ai';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ name: t.name, img: t.img });
      });
      return grouped;
    }
    return {};
  })();
  return (
    <section className="py-16 bg-sky-100/60">
      <div className="container mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="text-center mb-10"
        >
          <SectionHeading
            title={title}
            subtitle={subtitle}
            align="center"
          />
        </motion.div>

        <Tabs defaultValue="ai" data-testid={HOME.technologiesTabs}>
          <div className="flex justify-center">
            <TabsList className="bg-white border border-hairline p-1.5 rounded-full flex flex-wrap gap-1 h-auto">
              {techTabs.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="rounded-full px-4 py-2 text-xs font-semibold data-[state=active]:bg-gold-gradient data-[state=active]:text-white data-[state=active]:shadow-gold text-navy/70"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {techTabs.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-10">
              <motion.div
                key={t.id}
                variants={staggerParent(0.05)}
                initial="hidden"
                animate="show"
                className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-6"
              >
                {(techs[t.id] || []).map((tech) => (
                  <motion.div
                    key={tech.name}
                    variants={fadeUp}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="h-12 w-12 rounded-xl bg-white border border-hairline shadow-card flex items-center justify-center overflow-hidden gscale">
                      <img
                        src={tech.img}
                        alt=""
                        className="h-7 w-7 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement.innerHTML =
                            `<span class="text-[11px] font-bold text-navy">${tech.name.slice(0, 3).toUpperCase()}</span>`;
                        }}
                      />
                    </div>
                    <span className="mt-2 text-xs text-slatebody">{tech.name}</span>
                  </motion.div>
                ))}
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex justify-center mt-10">
          <PrimaryButton icon={<ArrowRight className="h-4 w-4" />}>Explore More</PrimaryButton>
        </div>
      </div>
    </section>
  );
}
